import React, { useEffect, useState } from 'react'
import { Form, Input, Button, Select, DatePicker, Checkbox, Spin, Modal, Switch, Upload, Row, Col, Card, Tooltip } from 'antd'
import { notify } from '../utils/notify'
import { getStates } from '../services/StateService'
import { UploadOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { validateForm } from '../utils/validateForms'
import { faxFrequency, faxBookingTypeSync, clinicPartnerTypes, faxSequencesV2, faxFrequency2, faxSequencesV3 } from '../utils/constants';
import { insertClinic } from '../services/ClinicsService'
import { getAllGHLTagMaster } from '../services/GHLTagMasterService'
import { updateStateOfTagMaster } from '../utils/GHLTagMasterUpdateState'
import ClinicsService from '../services/ClinicsService'
import useDebounce from '../hooks/useDebounce'
import dayjs from 'dayjs'

const { Option } = Select

const typesOfScreen = [
  { value: 'Obgyn', text: 'EPDS' },
  { value: 'PHQ9-GAD7', text: 'PHQ9-GAD7' },
  { value: 'Geriatric', text: 'GDS' }
]

const ehrSystem = ['AthenaHealth', 'eCW', 'eCW - Femwell', 'Epic', 'Cerner', 'AllScripts']

// Add new tech stack options
const electronicMedicalRecordSystems = ['AthenaHealth', 'eCW-Femwell', 'eCW', 'Epic', 'Cerner', 'Other', 'Unknown']
const patientIntakeSystems = ['AdvancedMD', 'Connect', 'eCW', 'Emitrr', 'Kyruus', 'Phreesia', 'Other', 'Unknown']
const patientEngagementSystems = ['Epic MyChart', 'athenahealth', 'NextGen Healthcare', 'eClinicalWorks', 'Klara', 'Relatient', 'TeleVox', 'Kareo', 'Other', 'Unknown']
const patientScreeningSystems = ['CHADIS', 'Neuroflow', 'Other', 'Unknown']

const CarePartnerForm = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [ehrName, setEhrName] = useState('')
  const [states, setStates] = useState([])
  const [sourceOrganizationType, setSourceOrganizationType] = useState([])
  const [sourceOrganizationName, setSourceOrganizationName] = useState([])
  const [specialty, setSpecialty] = useState([])
  const [fileList, setFileList] = useState([])
  const [isFaxScheduled, setIsFaxScheduled] = useState(false);
  const clinicsService = new ClinicsService();
  const [clinicCodeValue, setClinicCodeValue] = useState('')
  const [validatingClinicCode, setValidatingClinicCode] = useState(false)
  const [clinicCodeExists, setClinicCodeExists] = useState(false)
  const [phoneValue, setPhoneValue] = useState('')
  const [validatingPhone, setValidatingPhone] = useState(false)
  const [phoneConflict, setPhoneConflict] = useState(null)
  const [isNotOnboarded, setIsNotOnboarded] = useState(false)
  const [canTextPhone, setCanTextPhone] = useState(false)
  const [isBHI, setIsBHI] = useState(false)
  
  // Add state for tech stack selections
  const [emrSystem, setEmrSystem] = useState('')
  const [patientIntakeSystem, setPatientIntakeSystem] = useState('')
  const [patientEngagementSystem, setPatientEngagementSystem] = useState('')
  const [patientScreeningSystem, setPatientScreeningSystem] = useState('')
  
  // Add state for source organization "Other" selections
  const [sourceOrganizationTypeSelection, setSourceOrganizationTypeSelection] = useState('')
  const [sourceOrganizationNameSelection, setSourceOrganizationNameSelection] = useState('')
  
  // Add state for specialty "Other" selection
  const [specialtySelection, setSpecialtySelection] = useState('')
  
  // Debounce clinic code value
  const debouncedClinicCode = useDebounce(clinicCodeValue, 500)
  const debouncedPhone = useDebounce(phoneValue, 500)

  // Check for clinic code existence when debounced value changes
  useEffect(() => {
    // Don't check if clinic code is empty or less than 5 characters
    if (!debouncedClinicCode || debouncedClinicCode.length < 5) {
      setClinicCodeExists(false);
      return;
    }

    const checkClinicCode = async () => {
      setValidatingClinicCode(true);
      try {
        const exists = await clinicsService.checkClinicCodeExists(debouncedClinicCode);
        setClinicCodeExists(exists);

        // Manually trigger validation on the clinicCode field after we get the API response
        if (exists) {
          // Force validation to show the error message immediately
          form.validateFields(['clinicCode']);
        }
      } catch (error) {
        console.error('Error checking clinic code:', error);
      } finally {
        setValidatingClinicCode(false);
      }
    };

    checkClinicCode();
  }, [debouncedClinicCode, form]);

  // Check for duplicate phone number when debounced value changes.
  // The form accepts formatted phone "(XXX) XXX-XXXX" which is exactly 14 chars.
  // We also check raw 10-digit input in case the user hasn't triggered the formatter yet.
  useEffect(() => {
    const digits = (debouncedPhone || '').replace(/\D/g, '');
    if (digits.length < 10) {
      setPhoneConflict(null);
      return;
    }

    const checkPhone = async () => {
      setValidatingPhone(true);
      try {
        const { exists, clinic } = await clinicsService.checkPhoneExists(debouncedPhone);
        setPhoneConflict(exists ? clinic : null);
        if (exists) {
          form.validateFields(['phone']);
        }
      } catch (error) {
        console.error('Error checking phone:', error);
      } finally {
        setValidatingPhone(false);
      }
    };

    checkPhone();
  }, [debouncedPhone, form]);

  useEffect(() => {
    setLoading(true)
    loadData()
  }, [])

  const loadData = async () => {
    const state = await getStates()
    setStates(state)
    const ghlTagMasters = await getAllGHLTagMaster()
    updateStateOfTagMaster(
      ghlTagMasters,
      setSourceOrganizationType,
      setSourceOrganizationName,
      setSpecialty
    )
    setLoading(false)
  }
  const handleSwitchChange = (checked) => {
    setIsFaxScheduled(checked); 
  };

  const onFinish = async (data) => {
    setLoading(true);
    
    // Format the fax number for validation
    if (data.faxNumber) {
      const cleanFaxNumber = data.faxNumber.replace(/[^0-9]/g, '');
      if (cleanFaxNumber.length === 10) {
        // For display in the form we use (XXX) XXX-XXXX format
        // But for validateForm we need to ensure it can extract the digits
        data.faxNumber = cleanFaxNumber;
      }
    }
    
    const errors = validateForm(data)
    if (Object.keys(errors).length === 0) {
      // Retrieve the user object from local storage
      const user = JSON.parse(localStorage.getItem('user'))

      // Generate UUID for the clinic
      const generateUUID = () => {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
          return crypto.randomUUID();
        }
        // Fallback UUID v4 generator
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = Math.random() * 16 | 0;
          const v = c == 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      };

      const formData = new FormData()
      formData.append('clinicUuid', generateUUID())
      formData.append('name', data.name)
      data.streetName && formData.append('streetName', data.streetName)
      data.city && formData.append('city', data.city)
      formData.append('address', data.address)
      data.zip && formData.append('zip', data.zip)
      data.phone && formData.append('phone', data.phone)
      data.populationsServed && formData.append('populationsServed', data.populationsServed)
      formData.append('currentUser', user.name)
      formData.append('clinicCode', data.clinicCode)
      fileList?.length && data?.logo?.fileList.length > 0 && formData.append('logo', data.logo.fileList[0].originFileObj)
      data.faxNumber && formData.append('faxNumber', data.faxNumber)
      data.faxSequence && formData.append('faxSequence', data.faxSequence)
      // data.faxFrequency && formData.append('faxFrequency', data.faxFrequency)
      data.isFaxScheduled && formData.append('isFaxScheduled', data.isFaxScheduled)
      data.clinicans && formData.append('clinicans', data.clinicans)
      data.typesOfScreen && formData.append('typesOfScreen', data.typesOfScreen)
      
      // Handle "Other" options for source organization fields
      if (data.sourceOrganizationType) {
        const orgTypeValue = data.sourceOrganizationType === 'Other' && data.sourceOrganizationTypeOther 
          ? data.sourceOrganizationTypeOther 
          : data.sourceOrganizationType;
        formData.append('sourceOrganizationType', orgTypeValue);
      }
      
      if (data.sourceOrganizationName) {
        const orgNameValue = data.sourceOrganizationName === 'Other' && data.sourceOrganizationNameOther 
          ? data.sourceOrganizationNameOther 
          : data.sourceOrganizationName;
        formData.append('sourceOrganizationName', orgNameValue);
      }
      
      // Handle "Other" option for specialty
      if (data.specialty) {
        const specialtyValue = data.specialty === 'Other' && data.specialtyOther 
          ? data.specialtyOther 
          : data.specialty;
        formData.append('specialty', specialtyValue);
      }
      
      // data.faxBookingTypeSync && formData.append('faxBookingTypeSync', data.faxBookingTypeSync)
      data.partnerType && formData.append('partnerType', data.partnerType)
      
      // Add new fields
      data.portalAddDate && formData.append('portalAddDate', data.portalAddDate.format('DD-MM-YYYY'))
      if (data.clinicOnboardDate && !isNotOnboarded) {
        formData.append('clinicOnboardDate', data.clinicOnboardDate.format('DD-MM-YYYY'))
      }
      data.lunaJoyRep && formData.append('lunaJoyRep', data.lunaJoyRep)
      data.clinicPointOfContact && formData.append('clinicPointOfContactNotes', data.clinicPointOfContact)
      data.clinicPointOfContactPhone && formData.append('clinicPointOfContactPhone', data.clinicPointOfContactPhone)
      formData.append('canTextPhone', canTextPhone)
      formData.append('isBHI', isBHI)
      
      // Add tech stack fields
      // Handle "Other" options for tech stack fields
      if (data.patientIntakeSystem) {
        const intakeValue = data.patientIntakeSystem === 'Other' && data.patientIntakeSystemOther 
          ? data.patientIntakeSystemOther 
          : data.patientIntakeSystem;
        formData.append('patientIntakeSystem', intakeValue);
      }
      
      if (data.patientEngagementSystem) {
        const engagementValue = data.patientEngagementSystem === 'Other' && data.patientEngagementSystemOther 
          ? data.patientEngagementSystemOther 
          : data.patientEngagementSystem;
        formData.append('patientEngagementSystem', engagementValue);
      }
      
      if (data.patientScreeningSystem) {
        const screeningValue = data.patientScreeningSystem === 'Other' && data.patientScreeningSystemOther 
          ? data.patientScreeningSystemOther 
          : data.patientScreeningSystem;
        formData.append('patientScreeningSystem', screeningValue);
      }
      
      // Map electronicMedicalRecordSystem to ehr for backward compatibility
      if (data.electronicMedicalRecordSystem) {
        const ehrValue = data.electronicMedicalRecordSystem === 'Other' && data.electronicMedicalRecordSystemOther 
          ? data.electronicMedicalRecordSystemOther 
          : data.electronicMedicalRecordSystem;
        formData.append('ehr', ehrValue);
      }
      
      // Add EHR integration fields
      data.ehrId && formData.append('ehrId', data.ehrId)
      formData.append('typesOfIntegration', data.typesOfIntegration ? 'Sync Data' : 'None')
      data.referralLink && formData.append('referralLink', data.referralLink)
      data.allowReferral && formData.append('allowReferral', data.allowReferral)
      data.alias && formData.append('alias', data.alias)
      
      if (isFaxScheduled) {
        data.faxBookingTypeSync && formData.append('faxBookingTypeSync', data.faxBookingTypeSync)
        data.faxFrequency && formData.append('faxFrequency', data.faxFrequency)

        data.referralRecepientReportFrequency && formData.append('referralRecepientReportFrequency', data.referralRecepientReportFrequency)

        data.assessmentScreeningSequence && formData.append('assessmentScreeningSequence', data.assessmentScreeningSequence)
        data.assessmentScreeningFrequency && formData.append('assessmentScreeningFrequency', data.assessmentScreeningFrequency)

        data.referralStatusSequence && formData.append('referralStatusSequence', data.referralStatusSequence)
        data.referralStatusFrequency && formData.append('referralStatusFrequency', data.referralStatusFrequency)
      }
      try {
        await insertClinic(formData)
        notify(`"${data.name}" Clinic Added Successfully!`)
        navigate('/clinic')
      } catch (error) {
        Modal.error({
          title: 'Failed to add care partner',
          content: (
            <div>
              <p>{error?.response?.data?.error?.message}</p>
            </div>
          ),
          okText: 'Ok',
          cancelButtonProps: { style: { display: 'none' } }
        })
      }
      setLoading(false)
    } else {
      setLoading(false)
      const errorMessages = Object.values(errors).map((error, index) => {
        if (typeof error == 'object') {
          return error.map((err, i) => <p style={{ marginBottom: 0 }} key={`${i}_${index}`}>* {err}</p>);
        } else {
          return (<div key={index}>* {error}</div>)
        }
      })
      Modal.confirm({
        title: 'Error Message',
        content: (
          <div>
            {!errors.clinicans && <p>Oops! Can not update data due to the following errors:</p>}
            {errorMessages}
          </div>
        ),
        okText: 'Ok',
        cancelButtonProps: { style: { display: 'none' } }
      })
    }

    return true
  }

  const goBack = () => {
    navigate('/clinic')
  }

  const formItemLayout = {
    // Remove horizontal layout constraints
  }

  // Add a specific layout for fax scheduling section with longer labels
  const faxSchedulingLayout = {
    // Remove horizontal layout constraints for fax section
  }

  const getImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = document.createElement('img')
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      img.onload = () => {
        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0, img.width, img.height)

        const width = canvas.width
        const height = canvas.height

        resolve({ width, height })
      }

      img.onerror = () => {
        reject('Error loading image')
      }

      img.src = URL.createObjectURL(file)
    })
  }

  const handleFileUpload = async ({ file, onSuccess, onError }) => {
    if (file.size > 90 * 1024) {
      onError('File size should be less than 90kb')
      setFileList([])
      Modal.confirm({
        title: 'Error',
        content: (
          <div>
            <p>File size should be less than 90kb</p>
          </div>
        ),
        okText: 'Ok',
        cancelButtonProps: { style: { display: 'none' } }
      })
    } else {
      const dimensions = await getImageDimensions(file)
      if (dimensions.width <= 750 && dimensions.height <= 500) {
        onSuccess('ok')
        setFileList([file])
      } else {
        onError('This image does not meet the required resolution.')
        setFileList([])
        Modal.confirm({
          closable: false,
          title: 'Error',
          content: (
            <div>
              <p>This image does not meet the required resolution.</p>
            </div>
          ),
          okText: 'Ok',
          cancelButtonProps: { style: { display: 'none' } }
        })
      }
    }
  }

  // Clinic code validator
  const validateClinicCode = (_, value) => {
    if (!value) return Promise.resolve(); // Let the required validator handle empty value
    
    // Simplified validation - directly check if clinicCodeExists state is true
    if (clinicCodeExists && value.length >= 5) {
      return Promise.reject(new Error('This clinic code already exists. Please use a different code.'));
    }
    
    return Promise.resolve();
  };

  // Handle input change for clinic code
  const handleClinicCodeChange = (e) => {
    const value = e.target.value;
    setClinicCodeValue(value);
    
    // Reset the existence status when the user changes the input
    if (clinicCodeExists) {
      setClinicCodeExists(false);
    }
  };

  // Add validation functions
  const validatePhoneNumber = (_, value) => {
    if (!value) return Promise.resolve();

    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(value.replace(/[^\d]/g, ''))) {
      return Promise.reject(new Error('Please enter a valid 10-digit phone number'));
    }
    return Promise.resolve();
  };

  const validatePhoneUniqueness = (_, value) => {
    if (!value) return Promise.resolve();
    if (phoneConflict) {
      return Promise.reject(new Error(`A clinic with this phone number already exists${phoneConflict?.name ? ` (${phoneConflict.name})` : ''}`));
    }
    return Promise.resolve();
  };

  const validateFaxNumber = (_, value) => {
    if (!value) return Promise.resolve();
    
    // Strip all non-digit characters and check if we have 10 digits
    const cleanedValue = value.replace(/[^\d]/g, '');
    if (cleanedValue.length !== 10) {
      return Promise.reject(new Error('Please enter a valid 10-digit fax number'));
    }
    return Promise.resolve();
  };

  const validateZipCode = (_, value) => {
    if (!value) return Promise.resolve();
    
    // US ZIP code validation (5 digits or 5+4 format)
    const zipRegex = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
    if (!zipRegex.test(value)) {
      return Promise.reject(new Error('Please enter a valid ZIP code (00000 or 00000-0000 format)'));
    }
    return Promise.resolve();
  };

  const validateCity = (_, value) => {
    if (!value) return Promise.resolve();
    
    // City should only contain letters, spaces, hyphens and periods (for St.)
    const cityRegex = /^[a-zA-Z\s.\-]+$/;
    if (!cityRegex.test(value)) {
      return Promise.reject(new Error('Please enter a valid city name'));
    }
    return Promise.resolve();
  };

  // Add validation for clinic code to exclude special characters
  const validateNoSpecialCharsClinicCode = (_, value) => {
    if (!value) return Promise.resolve();
    
    // Only allow alphanumeric characters, hyphens and underscores for clinic code
    const validCodeRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validCodeRegex.test(value)) {
      return Promise.reject(new Error('Clinic code cannot contain special characters (only letters, numbers, hyphens and underscores allowed)'));
    }
    return Promise.resolve();
  };


  // Add a function to format phone numbers as they're typed
  const formatPhoneNumber = (value) => {
    if (!value) return value;
    
    // Strip all non-digit characters
    const phoneNumber = value.replace(/[^\d]/g, '');
    
    // Format: (XXX) XXX-XXXX
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  // Add a function to format fax numbers as they're typed (similar to phone)
  const formatFaxNumber = (value) => {
    if (!value) return value;
    
    // Strip all non-digit characters
    const faxNumber = value.replace(/[^\d]/g, '');
    
    // Format: (XXX) XXX-XXXX
    if (faxNumber.length <= 3) {
      return faxNumber;
    } else if (faxNumber.length <= 6) {
      return `(${faxNumber.slice(0, 3)}) ${faxNumber.slice(3)}`;
    } else {
      return `(${faxNumber.slice(0, 3)}) ${faxNumber.slice(3, 6)}-${faxNumber.slice(6, 10)}`;
    }
  };


  // Add validation for clinic onboard date
  const validateClinicOnboardDate = (_, value) => {
    if (!value && !isNotOnboarded) {
      return Promise.reject(new Error('Please enter a clinic onboard date or check "Not Onboarded"'));
    }
    return Promise.resolve();
  };

  const validateIntegersOnly = (_, value) => {
    if (!value) return Promise.resolve();
    const cleanedValue = value.replace(/[^\d]/g, '');
    if (cleanedValue !== value) {
      return Promise.reject(new Error('Please enter only numbers'));
    }
    return Promise.resolve();
  };


  return (
    <Spin spinning={loading || validatingClinicCode}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        width: '100%',
        padding: '0 16px'
      }}>
        <div style={{ 
          width: '100%',
          maxWidth: '1200px'
        }}>
          <Form 
            form={form} 
            onFinish={onFinish} 
            encType="multipart/form-data" 
            layout="vertical"
            labelAlign="left"
            colon={false}
            requiredMark="optional"
            scrollToFirstError
          >
            <Card 
              title="Care Partner Information" 
              style={{ 
                marginBottom: 24, 
                borderRadius: '8px', 
                border: '1px solid #d9d9d9',
                backgroundColor: '#f9f9ff'
              }}
              headStyle={{ 
                fontSize: '18px', 
                fontWeight: 'bold',
                backgroundColor: '#eceeff',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px'
              }}
            >
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="name"
                    label="Clinic/Referring Party Name"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter the care partner name.'
                      }
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="clinicCode"
                    label="Clinic URL Tag (Cannot be updated later e.g. referral.hellolunajoy.com/CareSource)"
                    validateStatus={clinicCodeExists ? "error" : undefined}
                    help={clinicCodeExists ? "This clinic code already exists. Please use a different code." : undefined}
                    rules={[
                      {
                        required: true,
                        message: 'Please enter the clinic code.'
                      },
                      {
                        min: 5, 
                        message: 'Clinic code must be at least 5 characters long.'
                      },
                      { validator: validateClinicCode },
                      { validator: validateNoSpecialCharsClinicCode }
                    ]}
                  >
                    <Input 
                      onChange={handleClinicCodeChange}
                      suffix={validatingClinicCode ? <Spin size="small" /> : null}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="sourceOrganizationType"
                    label="Partner Type"
                    rules={[
                      {
                        required: true,
                        message: 'Please select a partner type.'
                      }
                    ]}
                  >
                    <Select
                      placeholder="Select a partner type"
                      onChange={(value) => setSourceOrganizationTypeSelection(value)}
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {sourceOrganizationType.map((element) => (
                        <Option key={element} value={element}>
                          {element}
                        </Option>
                      ))}
                      <Option key="Other" value="Other">
                        Other
                      </Option>
                    </Select>
                  </Form.Item>
                  {sourceOrganizationTypeSelection === 'Other' && (
                    <Form.Item
                      name="sourceOrganizationTypeOther"
                      label="Specify Partner Type"
                      rules={[
                        {
                          required: true,
                          message: 'Please specify the partner type.'
                        }
                      ]}
                    >
                      <Input placeholder="Enter partner type" />
                    </Form.Item>
                  )}
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="sourceOrganizationName"
                    label="Corporate Entity"
                    rules={[
                      {
                        required: true,
                        message: 'Please select a corporate entity.'
                      }
                    ]}
                  >
                    <Select
                      placeholder="Select a corporate entity"
                      onChange={(value) => setSourceOrganizationNameSelection(value)}
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {sourceOrganizationName.map((element) => (
                        <Option key={element} value={element}>
                          {element}
                        </Option>
                      ))}
                      <Option key="Other" value="Other">
                        Other
                      </Option>
                    </Select>
                  </Form.Item>
                  {sourceOrganizationNameSelection === 'Other' && (
                    <Form.Item
                      name="sourceOrganizationNameOther"
                      label="Specify Corporate Entity"
                      rules={[
                        {
                          required: true,
                          message: 'Please specify the corporate entity.'
                        }
                      ]}
                    >
                      <Input placeholder="Enter corporate entity" />
                    </Form.Item>
                  )}
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} sm={8} md={6}>
                  <Form.Item 
                    name="specialty" 
                    label="Specialty"
                    rules={[
                      {
                        required: true,
                        message: 'Please select a specialty.'
                      }
                    ]}>
                    <Select
                      placeholder="Select a specialty"
                      onChange={(value) => setSpecialtySelection(value)}
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {specialty.map((element) => (
                        <Option key={element} value={element}>
                          {element}
                        </Option>
                      ))}
                      <Option key="Other" value="Other">
                        Other
                      </Option>
                    </Select>
                  </Form.Item>
                  {specialtySelection === 'Other' && (
                    <Form.Item
                      name="specialtyOther"
                      label="Specify Specialty"
                      rules={[
                        {
                          required: true,
                          message: 'Please specify the specialty.'
                        }
                      ]}
                    >
                      <Input placeholder="Enter specialty" />
                    </Form.Item>
                  )}
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Form.Item 
                    name="streetName" 
                    label="Address"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter the address.'
                      }
                    ]}
                  >
                    <Input placeholder="Enter address" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Form.Item 
                    name="city" 
                    label="City"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter the city.'
                      },
                      { validator: validateCity }
                    ]}
                  >
                    <Input placeholder="Enter city" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Form.Item
                    name="address"
                    label="State"
                    rules={[
                      {
                        required: true,
                        message: 'Please select a state.'
                      }
                    ]}
                  >
                    <Select
                      placeholder="Select a state"
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {states.map((state) => (
                        <Option key={state.code} value={state.code}>
                          {state.title}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} sm={8} md={6}>
                  <Form.Item 
                    name="zip" 
                    label="Zip"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter the zip code.'
                      },
                      { validator: validateZipCode }
                    ]}
                  >
                    <Input placeholder="Enter zip code" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Form.Item
                    name="phone"
                    label="Clinic Phone"
                    hasFeedback
                    validateStatus={validatingPhone ? 'validating' : (phoneConflict ? 'error' : undefined)}
                    rules={[
                      {
                        required: true,
                        message: 'Please enter the phone number.'
                      },
                      { validator: validatePhoneNumber },
                      { validator: validatePhoneUniqueness }
                    ]}
                  >
                    <Input
                      placeholder="Enter phone number"
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        form.setFieldsValue({ phone: formatted });
                        setPhoneValue(formatted);
                        // Clear conflict while the user is still typing so the error disappears immediately
                        if (phoneConflict) setPhoneConflict(null);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="populationsServed" label="Populations Served">
                    <Select
                      placeholder="Select populations served"
                      mode="multiple"
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {states.map((state) => (
                        <Option key={state.code} value={state.code}>
                          {state.title}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item 
                    name="faxNumber" 
                    label="Fax number"
                    rules={[
                      { validator: validateFaxNumber },
                      {
                        required: isFaxScheduled,
                        message: 'Fax number is required when fax scheduling is enabled'
                      }
                    ]}
                  >
                    <Input 
                      addonBefore="+1" 
                      onChange={(e) => {
                        const formatted = formatFaxNumber(e.target.value);
                        form.setFieldsValue({ faxNumber: formatted });
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="logo" label="Logo">
                    <Upload
                      maxCount={1}
                      fileList={fileList}
                      onRemove={(value) => {
                        setFileList([])
                      }}
                      customRequest={handleFileUpload}
                      listType="text"
                      accept=".jpg,.jpeg,.png"
                    >
                      <Button icon={<UploadOutlined />}>Upload Logo</Button>
                      <div>
                        <small>The image size must be less than 90 KB, and dimensions less than 750 x 500 pixels.</small>
                      </div>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="portalAddDate"
                    label="Portal Add Date"
                    rules={[
                      {
                        required: true,
                        message: 'Please select the portal add date.'
                      }
                    ]}
                    initialValue={dayjs()}
                  >
                    <DatePicker 
                      format="DD-MM-YYYY"
                      style={{ width: '100%' }}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="clinicOnboardDate"
                    label="Clinic Onboard Date"
                    rules={[
                      { validator: validateClinicOnboardDate }
                    ]}
                    style={{ marginBottom: 8 }}
                    required
                  >
                    <DatePicker 
                      format="DD-MM-YYYY"
                      style={{ width: '100%' }}
                      disabled={isNotOnboarded}
                    />
                  </Form.Item>
                  <Form.Item
                    name="notOnboarded"
                    valuePropName="checked"
                    style={{ marginTop: -4 }}
                  >
                    <Checkbox 
                      onChange={(e) => {
                        setIsNotOnboarded(e.target.checked);
                        if (e.target.checked) {
                          form.setFieldsValue({ clinicOnboardDate: null });
                        }
                      }}
                    >
                      Not Onboarded{' '}
                      <Tooltip title="Clinics are considered onboarded the date when they agree to a formal relationship with LunaJoy and have a LunaJoy rep">
                        <QuestionCircleOutlined style={{ color: '#1890ff', marginLeft: 4 }} />
                      </Tooltip>
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="lunaJoyRep"
                    label="LunaJoy Representative"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter the LunaJoy representative.'
                      }
                    ]}
                  >
                    <Input placeholder="Enter LunaJoy representative" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="clinicPointOfContact"
                    label="Clinic Point of Contact & Notes"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter the clinic point of contact and notes.'
                      }
                    ]}
                  >
                    <Input.TextArea 
                      placeholder="Enter clinic point of contact and notes"
                      rows={3}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="clinicPointOfContactPhone"
                    label="Clinic Point of Contact Phone"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter the clinic point of contact phone number.'
                      },
                      { validator: validatePhoneNumber }
                    ]}
                     style={{ marginBottom: 8 }}
                  >
                    <Input 
                      placeholder="Enter phone number"
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        form.setFieldsValue({ clinicPointOfContactPhone: formatted });
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="canTextPhone"
                    valuePropName="checked"
                    style={{ marginTop: 8 }}
                  >
                    <Checkbox 
                      onChange={(e) => setCanTextPhone(e.target.checked)}
                    >
                      Can we text this phone number?
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
              <Col xs={24} md={12}>
                  <Form.Item name="partnerType" label="Clinic Partner Type">
                    <Select placeholder="Select a clinic partner type">
                      {clinicPartnerTypes?.map((type) => (
                        <Option key={type.value} value={type.value}>
                          {type.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="typesOfScreen" label="Type of screener">
                    <Select placeholder="Select a screener type" mode="multiple">
                      {typesOfScreen.map((screener) => (
                        <Option key={screener.value} value={screener.value}>
                          {screener.text}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item name="clinicans" label="Clinicians"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value || value.length === 0) return Promise.resolve();

                          // Normalize spaces: trim and collapse multiple spaces to one
                          const normalizedValue = value.map(clinician =>
                            clinician
                              .replace(/\s+/g, ' ') // collapse multiple spaces
                              .trim()
                          );

                          // Check for numbers in any clinician name
                          const invalidNames = normalizedValue.filter(
                            clinician => /\d/.test(clinician)
                          );

                          if (invalidNames.length > 0) {
                            return Promise.reject(
                              new Error('Clinician names cannot contain numbers')
                            );
                          }
                          form.setFieldsValue({ clinicans: normalizedValue });

                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <Select mode="tags" placeholder="Add clinicians" tokenSeparators={[',']} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="isFaxScheduled" label="Schedule fax">
                    <Switch onChange={handleSwitchChange} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="isBHI"
                    label="BHI Clinic"
                    valuePropName="checked"
                    tooltip="Mark this clinic as a Behavioral Health Integration (BHI) clinic. Referrals from BHI clinics are flagged in GHL so they can be routed to BHI Coaches."
                  >
                    <Switch
                      checkedChildren="BHI"
                      unCheckedChildren="No"
                      onChange={(checked) => setIsBHI(checked)}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Clinic Tech Section */}
            <Card 
              title="Clinic Tech" 
              style={{ 
                marginBottom: 24, 
                borderRadius: '8px', 
                border: '1px solid #d9d9d9',
                backgroundColor: '#fff9f9'
              }}
              headStyle={{ 
                fontSize: '18px', 
                fontWeight: 'bold',
                backgroundColor: '#ffecec',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px'
              }}
            >
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="electronicMedicalRecordSystem"
                    label="Electronic Medical Record System"
                  >
                    <Select
                      placeholder="Select EMR system"
                      onChange={(value) => setEmrSystem(value)}
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {electronicMedicalRecordSystems.map((system) => (
                        <Option key={system} value={system}>
                          {system}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {emrSystem === 'Other' && (
                    <Form.Item
                      name="electronicMedicalRecordSystemOther"
                      label="Specify EMR System"
                      rules={[
                        {
                          required: true,
                          message: 'Please specify the EMR system.'
                        }
                      ]}
                    >
                      <Input placeholder="Enter EMR system name" />
                    </Form.Item>
                  )}
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="patientIntakeSystem"
                    label="Patient Intake System"
                  >
                    <Select
                      placeholder="Select patient intake system"
                      onChange={(value) => setPatientIntakeSystem(value)}
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {patientIntakeSystems.map((system) => (
                        <Option key={system} value={system}>
                          {system}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {patientIntakeSystem === 'Other' && (
                    <Form.Item
                      name="patientIntakeSystemOther"
                      label="Specify Patient Intake System"
                      rules={[
                        {
                          required: true,
                          message: 'Please specify the patient intake system.'
                        }
                      ]}
                    >
                      <Input placeholder="Enter patient intake system name" />
                    </Form.Item>
                  )}
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="patientEngagementSystem"
                    label="Patient Engagement System"
                  >
                    <Select
                      placeholder="Select patient engagement system"
                      onChange={(value) => setPatientEngagementSystem(value)}
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {patientEngagementSystems.map((system) => (
                        <Option key={system} value={system}>
                          {system}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {patientEngagementSystem === 'Other' && (
                    <Form.Item
                      name="patientEngagementSystemOther"
                      label="Specify Patient Engagement System"
                      rules={[
                        {
                          required: true,
                          message: 'Please specify the patient engagement system.'
                        }
                      ]}
                    >
                      <Input placeholder="Enter patient engagement system name" />
                    </Form.Item>
                  )}
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="patientScreeningSystem"
                    label="Patient Screening System"
                  >
                    <Select
                      placeholder="Select patient screening system"
                      onChange={(value) => setPatientScreeningSystem(value)}
                      filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {patientScreeningSystems.map((system) => (
                        <Option key={system} value={system}>
                          {system}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {patientScreeningSystem === 'Other' && (
                    <Form.Item
                      name="patientScreeningSystemOther"
                      label="Specify Patient Screening System"
                      rules={[
                        {
                          required: true,
                          message: 'Please specify the patient screening system.'
                        }
                      ]}
                    >
                      <Input placeholder="Enter patient screening system name" />
                    </Form.Item>
                  )}
                </Col>
              </Row>
            </Card>

            {/* Fax Scheduling Section */}
            {isFaxScheduled && (
              <Card 
                title="Fax Scheduling Configuration" 
                style={{ 
                  marginBottom: 24, 
                  borderRadius: '8px', 
                  border: '1px solid #d9d9d9',
                  backgroundColor: '#f9fff9'
                }}
                headStyle={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold',
                  backgroundColor: '#ecffec',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px'
                }}
              >
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="faxBookingTypeSync"
                      label="Review Report Sequence"
                      {...faxSchedulingLayout}
                      rules={[
                        {
                          required: isFaxScheduled,
                          message: "Please select review report sequence",
                        },
                      ]}
                    >
                      <Select placeholder="Select a review report sequence">
                        {faxBookingTypeSync?.map((type) => (
                          <Option key={type.value} value={type.value}>
                            {type.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.faxBookingTypeSync !== currentValues.faxBookingTypeSync
                      }
                    >
                      {({ getFieldValue }) =>
                        getFieldValue("faxBookingTypeSync") ? (
                          <Form.Item
                            name="faxFrequency"
                            label="Review Report Frequency"
                            {...faxSchedulingLayout}
                            rules={[
                              {
                                required: isFaxScheduled,
                                message: "Please select review report frequency",
                              },
                            ]}
                          >
                            <Select placeholder="Select a review report frequency">
                              {faxFrequency?.map((freq) => (
                                <Option key={freq.value} value={freq.value}>
                                  {freq.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        ) : null
                      }
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item 
                      name="referralRecepientReportFrequency" 
                      label="Referral Recipient Report Frequency"
                      {...faxSchedulingLayout}
                      rules={[{
                        required: isFaxScheduled,
                        message: 'Please select referral recipient report frequency'
                      }]}
                    >
                      <Select placeholder="Select Referral Recipient Report Frequency">
                        {faxFrequency2?.map((freq) => (
                          <Option key={freq.value} value={freq.value}>
                            {freq.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="assessmentScreeningSequence"
                      label="Assessment Screening Sequence"
                      {...faxSchedulingLayout}
                      rules={[
                        {
                          required: isFaxScheduled,
                          message: "Please select assessment screening sequence",
                        },
                      ]}
                    >
                      <Select placeholder="Select an assessment screening sequence">
                        {faxSequencesV3.map((fax) => (
                          <Option key={fax} value={fax}>
                            {fax}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.assessmentScreeningSequence !==
                        currentValues.assessmentScreeningSequence
                      }
                    >
                      {({ getFieldValue }) =>
                        getFieldValue("assessmentScreeningSequence") ? (
                          <Form.Item
                            name="assessmentScreeningFrequency"
                            label="Assessment Screening Frequency"
                            {...faxSchedulingLayout}
                            rules={[
                              {
                                required: isFaxScheduled,
                                message: "Please select assessment screening frequency",
                              },
                            ]}
                          >
                            <Select placeholder="Select Assessment Screening Frequency">
                              {faxFrequency?.map((freq) => (
                                <Option key={freq.value} value={freq.value}>
                                  {freq.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        ) : null
                      }
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="referralStatusSequence"
                      label="Referral Status Sequence"
                      {...faxSchedulingLayout}
                      rules={[
                        {
                          required: isFaxScheduled,
                          message: "Please select referral status sequence",
                        },
                      ]}
                    >
                      <Select placeholder="Select a referral status sequence">
                        {faxSequencesV2.map((fax) => (
                          <Option key={fax} value={fax}>
                            {fax}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.referralStatusSequence !== currentValues.referralStatusSequence
                      }
                    >
                      {({ getFieldValue }) =>
                        getFieldValue("referralStatusSequence") ? (
                          <Form.Item
                            name="referralStatusFrequency"
                            label="Referral Status Frequency"
                            {...faxSchedulingLayout}
                            rules={[
                              {
                                required: isFaxScheduled,
                                message: "Please select referral status frequency",
                              },
                            ]}
                          >
                            <Select placeholder="Select referral status frequency">
                              {faxFrequency2?.map((freq) => (
                                <Option key={freq.value} value={freq.value}>
                                  {freq.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        ) : null
                      }
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            )}

            <Card 
              title="EHR Integration" 
              style={{ 
                marginBottom: 24, 
                borderRadius: '8px', 
                border: '1px solid #d9d9d9',
                backgroundColor: '#fdf9ff'
              }}
              headStyle={{ 
                fontSize: '18px', 
                fontWeight: 'bold',
                backgroundColor: '#f5ecff',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px'
              }}
            >
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item name="alias" label="Clinic Name in EHR">
                    <Input />
                  </Form.Item>
                  <div>
                    <small>*(Provide the clinic name sent by EHR)</small>
                  </div>
                </Col>
              </Row>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.electronicMedicalRecordSystem !== currentValues.electronicMedicalRecordSystem
                }
              >
                {({ getFieldValue }) => {
                  const emrSystem = getFieldValue("electronicMedicalRecordSystem");
                  const isAthenaHealth = emrSystem === 'AthenaHealth' || (emrSystem === 'Other' && getFieldValue("electronicMedicalRecordSystemOther")?.toLowerCase().includes('athena'));
                  
                  return isAthenaHealth ? (
                    <>
                      <div style={{ 
                        marginTop: 16, 
                        marginBottom: 16, 
                        fontWeight: 'bold',
                        padding: '8px 12px',
                        backgroundColor: '#f9f0ff',
                        borderRadius: '4px'
                      }}>
                        AthenaHealth Configuration
                      </div>
                      <Row gutter={24}>
                        <Col xs={24} md={12}>
                          <Form.Item name="ehrId" label="Practice ID" rules={[
                            { validator: validateIntegersOnly }
                          ]}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item 
                            name="typesOfIntegration" 
                            label="Enable sync"
                            valuePropName="checked"
                            style={{ marginBottom: 16 }}
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col xs={24} md={12}>
                          <Form.Item 
                            label="Athena referral" 
                            name="allowReferral" 
                            valuePropName="checked"
                          >
                            <Checkbox>Enable Athena referral functionality</Checkbox>
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  ) : null;
                }}
              </Form.Item>
            </Card>

            {/* Back and Submit Buttons */}
            <Row>
              <Col xs={24} style={{ textAlign: 'right',  marginBottom: 40 }}>
                <Button size="large" onClick={goBack} style={{ marginRight: 10, fontWeight: 'semibold' }}>Back</Button>
                <Button 
                  size="large" 
                  type="primary" 
                  htmlType="submit" 
                  style={{ padding: '0 30px', fontWeight: 'semibold' }}
                >
                  Submit
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
    </Spin>
  )
}

export default CarePartnerForm
